const DAY = 86_400_000;

export type StoreReviewHistory = {
  firstUse: number;
  activeDays: number[];
  lastAttempt: number | null;
  lastVersion: string | null;
};

export function recordActiveDay(previous: StoreReviewHistory | null, now: number): StoreReviewHistory {
  const today = Math.floor(now / DAY);
  return {
    firstUse: previous?.firstUse ?? now,
    activeDays: [...new Set([...(previous?.activeDays ?? []), today])]
      .filter(day => day >= today - 89 && day <= today).sort((a, b) => a - b),
    lastAttempt: previous?.lastAttempt ?? null,
    lastVersion: previous?.lastVersion ?? null,
  };
}

// Store ratings are independent of in-app feedback sentiment. Only sustained
// usage and frequency limits determine eligibility.
export function storeReviewDue(history: StoreReviewHistory, version: string, now: number): boolean {
  if (!version || now - history.firstUse < 30 * DAY || history.lastVersion === version) return false;
  if (history.lastAttempt !== null && now - history.lastAttempt < 180 * DAY) return false;
  const today = Math.floor(now / DAY);
  const days = [...new Set(history.activeDays)].filter(day => day >= today - 89 && day <= today);
  const recentDays = days.filter(day => day >= today - 13);
  // Use weeks relative to the first observed use, so a Sunday/Monday boundary
  // cannot make two adjacent days look like weeks of established use.
  const firstDay = Math.floor(history.firstUse / DAY);
  const weeks = new Set(days.map(day => Math.floor((day - firstDay) / 7)));
  return days.length >= 10 && weeks.size >= 3 && recentDays.length >= 4;
}

export function parseStoreReviewHistory(raw: string): StoreReviewHistory {
  const value = JSON.parse(raw);
  if (!value || !Number.isFinite(value.firstUse) || value.firstUse < 0
    || !Array.isArray(value.activeDays) || value.activeDays.length > 90
    || !value.activeDays.every((day: unknown) => Number.isSafeInteger(day) && Number(day) >= 0)
    || !(value.lastAttempt === null || (Number.isFinite(value.lastAttempt) && value.lastAttempt >= 0))
    || !(value.lastVersion === null || typeof value.lastVersion === 'string')) {
    throw new Error('Invalid store review history');
  }
  return value;
}
